
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { Driver } from '@/models/Driver.model';

// --- Services ---
import AppService from '@/services/app';
import DriverService from '@/services/driver';
import DriverTypeService from '@/services/driverType';

// --- Third Party ---
import dayjs from 'dayjs';

@Component({
  components: {
    DownloadExcel,
  },
})
export default class DriverListing extends Vue {
  // used by Filter Area
  search: string = '';
  filterOptions: string[] = ['Active', 'PDP Due', 'Deleted'];
  driverFilter: string = 'Active';
  sortBy: string = 'nickname';
  sortDescending: boolean = false;

  // used by No Data Slot
  isLoading: boolean = true;
  displayDrivers: Driver[] = [];

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: Driver = Object.assign({}, this.$store.getters['drivers/emptyDriver']);

  valid: boolean = false;

  // used for creating a new driver
  pdpPicker: boolean = false;
  trainingDatePicker: boolean = false;
  startDatePicker: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredNumber: (value: number) => value != null && value !== 0 || '',
    name: (value: string) => /^(?=.*[a-zA-Z- ])[a-zA-Z- ]+$/.test(value) || 'Remove invalid characters',
    nickname: (value: string) => /^(?=.*[a-zA-Z0-9- _.@])[a-zA-Z0-9- _.@]+$/.test(value) || 'Remove invalid characters',
    optionalCellNumber: (value: string) => (value != null && value.length > 0) ?  (/^[0-9]{10}$/.test(value) || 'Please enter a valid Phone Number') : true,
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
  };

  clicked: boolean = false;

  // used by Confirm Action Dialog for Delete Item
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      if (this.driverFilter === 'Deleted') {
        return [
          {
            text: 'Nickname',
            value: 'nickname',
          },
          {
            text: 'Name',
            value: 'name',
          },        
          {
            text: 'Cellphone Number',
            value: 'cellNumber',
            sortable: false,
          },
          {
            text: 'Type',
            value: 'type',
          },
          {
            text: 'Deleted At',
            value: 'deletedAt',
          },
          
          {
            text: 'Deleted By',
            value: 'deletedBy',
          },
          {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
        ];
      } else {
        return [  
          {
            sortable: false,
            text: '',
            value: 'onLeaveToday',
          },
          {
            text: 'Nickname',
            value: 'nickname',
          },
          {
            text: 'Name',
            value: 'name',
          },        
          {
            sortable: false,
            text: 'Cell Number',
            value: 'cellNumber',
          },
          {
            text: 'Type',
            value: 'type',
          },
          {
            text: 'PDP Expiry',
            value: 'pdpExpiry',
          },
          {
            text: 'Start Date',
            value: 'startDate',
          },       
          {
            text: 'Training Date',
            value: 'trainingDate',
          },
          {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
        ];
      }
    }

    async beforeMount() {
      // update filter list:
      if (this.restrictedFilterFunctionality()) {
        this.filterOptions = ['Active'];
      }

      await DriverTypeService.listAll(false);
      await this.listActive(true);

      this.filterDrivers();
    }

    getToday() {
      return dayjs().format('YYYY-MM-DD');
    }

    showWarning(type: string, item: Driver) {
      if (!item.deletedAt) {
        switch (type) {
          case 'pdp':
            const pdpExpiryDate = dayjs(item.pdpExpiry);

            if (dayjs().isAfter(pdpExpiryDate)) {
              return 'overdue';
            } else if (dayjs().isAfter(pdpExpiryDate.subtract(21, 'days'))) {
              return 'pdp-due';
            }
        }
      }
      return '';
    }

    async listActive(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await DriverService.listActive(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async listDeleted(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await DriverService.listDeleted(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async filterDrivers() {
      switch (this.driverFilter) {
        case ('Active'):
          await this.listActive(false);
          this.displayDrivers = this.$store.getters['drivers/activeDrivers'];
          this.sortBy = 'nickname';
          this.sortDescending = false;
          break;

        case ('PDP Due'):
          this.displayDrivers = this.$store.getters['drivers/activeDrivers'].filter((item: Driver) => dayjs().isAfter(dayjs(item.pdpExpiry).subtract(21, 'days')));
          this.sortBy = 'pdpExpiry';
          this.sortDescending = false;
          break;

        case ('Deleted'):
          await this.listDeleted(false);
          this.displayDrivers = this.$store.getters['drivers/deletedDrivers'];
          this.sortBy = 'deletedAt';
          this.sortDescending = true;
          break;
      }
    }

    restrictedFilterFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 4},
        { type: 'Operator', level: 3},
      ]);
    }

    allowRestoreFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 4},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion

  // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.displayDrivers.reduce((a: any, b: any) => {
        let newItem = b;
        newItem = Object.assign({}, b, { 
          cellNumber: b.cellNumber + (b.cellNumber2 ? ', ' + b.cellNumber2 : ''),
          hasReceipt: (b.hasReceipt ? 'YES' : 'NO') 
        });

        return a.concat(newItem);
      }, []);
    }

    getFieldsForCSV() {
      return {
        'Nickname': 'nickname',
        'Name': 'name',
        'Cell Number': 'cellNumber',
        'Type': 'type',
        'PDP Expiry': 'pdpExpiry',
        'Has Receipt': 'hasReceipt',
        'Start Date': 'startDate',
        'Training Date': 'trainingDate',
      };
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    async launchNewItemDialog() {
      this.clickedItem = Object.assign({}, this.$store.getters['drivers/emptyDriver']);

      this.valid = false;
      this.title = 'Create New Driver';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    launchUpdateItemDialog(item: Driver) {
      this.clickedItem = Object.assign({}, item);

      this.title = 'Update ' + (this.clickedItem.name);

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await DriverService.create({
          typeId: this.clickedItem.typeId,
          linkBonus: this.clickedItem.linkBonus,
          name: this.clickedItem.name,
          nickname: this.clickedItem.nickname,
          cellNumber: this.clickedItem.cellNumber,
          cellNumber2: this.clickedItem.cellNumber2,
          idNumber: this.clickedItem.idNumber,
          pdpExpiry: this.clickedItem.pdpExpiry,
          hasReceipt: this.clickedItem.hasReceipt,
          startDate: this.clickedItem.startDate,
          trainingDate: this.clickedItem.trainingDate,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listActive(true);
            this.driverFilter = 'Active';
            this.filterDrivers();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Drivers.vue - doCreate - 908 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;
        this.confirmActionDelay = true;

        const response = await DriverService.update({
          id: this.clickedItem.id,
          typeId: this.clickedItem.typeId,
          linkBonus: this.clickedItem.linkBonus,
          name: this.clickedItem.name,
          nickname: this.clickedItem.nickname,
          cellNumber: this.clickedItem.cellNumber,
          cellNumber2: this.clickedItem.cellNumber2,
          idNumber: this.clickedItem.idNumber,
          pdpExpiry: this.clickedItem.pdpExpiry,
          hasReceipt: this.clickedItem.hasReceipt,
          startDate: this.clickedItem.startDate,
          trainingDate: this.clickedItem.trainingDate,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listActive(true);
            this.filterDrivers();
            this.showItemDialog = false;
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Drivers.vue - doUpdate - 957 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
        this.confirmActionDelay = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
    launchDeleteItemDialog(item: Driver) {
      this.clickedItem = item;

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212 ">delete</span> the Driver named <span style="font-weight: 700">' 
        + item.nickname + ' (' + item.name + ')</span>.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchRestoreItemDialog(item: Driver) {
      this.clickedItem = item;

      this.action = 'RESTORE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: green">restore</span> the Driver named <span style="font-weight: 700">' 
        + item.nickname + ' (' + item.name + ')</span>.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      } else if (this.action === 'RESTORE') {
        await this.doRestore();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listActive(true);
            await this.listDeleted(true);
            this.filterDrivers();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Drivers.vue - doDelete - 1028 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doRestore() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverService.restore({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listActive(true);
            await this.listDeleted(true);
            this.filterDrivers();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Drivers.vue - doRestore - 1064 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}

